import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, Select, Switch, message, Space, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import useCompanyNavigate from '../../utils/useCompanyNavigate';
import { storeApi, uploadApi } from '../../services/api';

// 门店形象照片上传（COS）
const StoreImageUploader = ({ value = {}, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const customRequest = async ({ file, onSuccess, onError }) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await uploadApi.uploadImage(formData);
      onChange({ url: res.data.url });
      message.success('照片上传成功');
      onSuccess(res);
    } catch (err) {
      message.error(err.message || '照片上传失败');
      onError(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Upload accept="image/jpeg,image/png,image/webp" listType="picture-card" showUploadList={false} customRequest={customRequest}>
      {value?.url ? (
        <img src={value.url} alt="门店照片" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : (
        <div>
          <UploadOutlined />
          <div style={{ marginTop: 8 }}>{uploading ? '上传中...' : '上传照片'}</div>
        </div>
      )}
    </Upload>
  );
};

const StoreForm = () => {
  const navigate = useCompanyNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id);

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  const fetchData = async () => {
    setInitialLoading(true);
    try {
      const res = await storeApi.detail(id);
      if (res.success) {
        const d = res.data;
        form.setFieldsValue({
          ...d,
          // 经纬度转数字，确保回显到输入框
          lat: d.lat !== null && d.lat !== undefined ? Number(d.lat) : undefined,
          lng: d.lng !== null && d.lng !== undefined ? Number(d.lng) : undefined,
          showFrontend: d.show_frontend !== 0,
          image: d.image_url ? { url: d.image_url } : {},
        });
      }
    } catch (error) {
      message.error('获取门店信息失败');
      navigate('/admin/stores');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        name: values.name,
        address: values.address,
        phone: values.phone,
        lat: values.lat ?? null,
        lng: values.lng ?? null,
        imageUrl: values.image?.url || null,
        status: values.status,
        showFrontend: values.showFrontend ? 1 : 0,
      };
      if (id) {
        await storeApi.update(id, payload);
        message.success('更新成功');
      } else {
        await storeApi.create(payload);
        message.success('创建成功');
      }
      navigate('/admin/stores');
    } catch (error) {
      message.error(id ? '更新失败' : '创建失败');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2>{id ? '编辑门店' : '新增门店'}</h2>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ status: 1, showFrontend: true }}
        style={{ maxWidth: 600 }}
      >
        <Form.Item
          name="name"
          label="门店名称"
          rules={[{ required: true, message: '请输入门店名称' }]}
        >
          <Input placeholder="请输入门店名称" />
        </Form.Item>

        <Form.Item name="address" label="门店地址">
          <Input placeholder="请输入门店地址" />
        </Form.Item>

        <Form.Item name="phone" label="联系电话">
          <Input placeholder="请输入联系电话" />
        </Form.Item>

        <Form.Item name="image" label="门店形象照片">
          <StoreImageUploader />
        </Form.Item>

        <Form.Item name="lat" label="纬度" extra="用于小程序计算距离和地图导航；可在腾讯地图坐标拾取器获取">
          <InputNumber style={{ width: '100%' }} placeholder="如 39.9042" />
        </Form.Item>

        <Form.Item name="lng" label="经度">
          <InputNumber style={{ width: '100%' }} placeholder="如 116.4074" />
        </Form.Item>

        <Form.Item name="status" label="状态">
          <Select>
            <Select.Option value={1}>启用</Select.Option>
            <Select.Option value={0}>禁用</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item name="showFrontend" label="显示在前端（小程序门店列表）" valuePropName="checked"
          extra="关闭后，该门店不会出现在小程序的门店列表中">
          <Switch />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              {id ? '更新' : '创建'}
            </Button>
            <Button onClick={() => navigate('/admin/stores')}>
              取消
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default StoreForm;
